if (!customElements.get('store-lp-order')) {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted || window.Shopify?.designMode || window.location.hash) return;
    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation && navigation.type !== 'navigate') return;
    const target = document.getElementById('StoreLpAbout');
    if (!target) return;
    const offset = -(parseFloat(getComputedStyle(target).scrollMarginTop) || 0);
    if (window.lenis) window.lenis.scrollTo(target, { immediate: true });
    else window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top + offset, behavior: 'instant' });
  });

  customElements.define(
    'store-lp-order',
    class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.config = JSON.parse(this.querySelector('[data-order-config]').textContent);
        this.form = this.querySelector('[data-order-form]');
        this.file = this.querySelector('[data-prescription-file]');
        this.buttons = [...this.querySelectorAll('[data-purchase], [data-sticky-purchase]')];
        this.busy = false;
        this.form.addEventListener('change', () => this.update());
        this.form.addEventListener('submit', (event) => this.submit(event));
        this.querySelector('[data-sticky-purchase]').addEventListener('click', () => {
          if (!this.canSubmit && !this.busy) {
            const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (window.lenis) window.lenis.scrollTo(0, { immediate: reduceMotion });
            else window.scrollTo({ top: 0, behavior: reduceMotion ? 'instant' : 'smooth' });
            return;
          }
          if (!this.form.reportValidity()) {
            this.form.querySelector(':invalid')?.scrollIntoView({ block: 'center' });
            return;
          }
          this.form.requestSubmit();
        });
        this.querySelector('[data-return-to-options]').addEventListener('click', (event) => {
          if (this.selection().lens || this.busy) return;
          event.preventDefault();
          const target = this.querySelector('.lp-order__body');
          const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (window.lenis) window.lenis.scrollTo(target, { offset: -104, immediate: reduceMotion });
          else
            window.scrollTo({
              top: window.scrollY + target.getBoundingClientRect().top - 104,
              behavior: reduceMotion ? 'instant' : 'smooth',
            });
        });
        const track = this.querySelector('.lp-gallery__track');
        const slides = [...track.children];
        const padding = () => parseFloat(getComputedStyle(track).paddingLeft) || 0;
        const current = () =>
          slides.reduce(
            (best, slide, index) =>
              Math.abs(slide.offsetLeft - track.scrollLeft - padding()) <
              Math.abs(slides[best].offsetLeft - track.scrollLeft - padding())
                ? index
                : best,
            0,
          );
        const move = (delta) =>
          track.scrollTo({
            left: slides[Math.max(0, Math.min(slides.length - 1, current() + delta))].offsetLeft - padding(),
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          });
        this.querySelectorAll('[data-gallery-index]').forEach((button) => {
          button.addEventListener('click', () => move(Number(button.dataset.galleryIndex) - current()));
        });
        this.querySelector('[data-gallery-prev]').addEventListener('click', () => move(-1));
        this.querySelector('[data-gallery-next]').addEventListener('click', () => move(1));
        track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            move(event.key === 'ArrowLeft' ? -1 : 1);
          }
        });
        track.addEventListener(
          'scroll',
          () => {
            this.querySelector('[data-gallery-counter]').textContent = `${current() + 1}/${slides.length}`;
            this.querySelectorAll('[data-gallery-index]').forEach((button, index) =>
              button.setAttribute('aria-current', String(index === current())),
            );
          },
          { passive: true },
        );
        this.update();
      }
      money(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'ja', {
          style: 'currency',
          currency: this.config.currency || 'JPY',
        }).format(cents / 100);
      }
      variant(kind) {
        const option = this.querySelector(`[data-variant="${kind}"]`)?.selectedOptions[0];
        return option
          ? {
              id: Number(option.value),
              price: Number(option.dataset.price),
              available: option.dataset.available === 'true',
            }
          : this.config[kind];
      }
      selection() {
        return {
          lens: this.form.querySelector('[name="lens"]:checked')?.value,
          sun: this.form.querySelector('[name="accessory"]:checked')?.value === 'sunglasses',
        };
      }
      update() {
        const selection = this.selection();
        const prescription = selection.lens === 'prescription';
        this.querySelector('[data-prescription]').hidden = !prescription;
        this.file.disabled = !prescription;
        this.file.required = prescription;
        const file = this.file.files[0];
        this.file.setCustomValidity(
          prescription &&
            file &&
            (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type) || file.size > 10 * 1024 * 1024)
            ? this.config.messages.file
            : '',
        );
        const sunVariants = this.querySelector('[data-sun-variants]');
        if (sunVariants) sunVariants.hidden = !selection.sun;
        const base = this.variant('base'),
          lens = this.variant('lens'),
          sun = this.variant('sun');
        const values = {
          total: base.price + (prescription ? lens.price : 0) + (selection.sun ? sun.price : 0),
          'base-total': base.price,
          'lens-total': lens.price,
          'sun-total': sun.price,
          'lens-price': lens.price,
          'sun-price': sun.price,
        };
        Object.entries(values).forEach(([key, price]) =>
          this.querySelectorAll(`[data-${key}]`).forEach((el) => {
            el.textContent = this.money(price);
          }),
        );
        this.querySelectorAll('[data-lens-breakdown]').forEach((row) => {
          row.hidden = !prescription;
        });
        this.querySelectorAll('[data-sun-breakdown]').forEach((row) => {
          row.hidden = !selection.sun;
        });
        this.querySelector('[data-shipping]').textContent = prescription
          ? this.config.messages.prescriptionShipping
          : this.config.shipping;
        const available =
          base.id &&
          base.available &&
          (!prescription || (lens.id && lens.available)) &&
          (!selection.sun || (sun.id && sun.available));
        const key = !selection.lens
          ? 'choose'
          : !available
            ? 'unavailable'
            : prescription && (!file || !this.file.validity.valid)
              ? 'upload'
              : this.busy
                ? 'loading'
                : 'ready';
        this.buttons.forEach((button) => {
          button.textContent = this.config.messages[key];
          const returnToTop =
            !this.busy &&
            ((button.hasAttribute('data-sticky-purchase') && key !== 'ready') ||
              (button.hasAttribute('data-return-to-options') && key === 'choose'));
          button.classList.toggle('lp-purchase--return-top', returnToTop);
          button.disabled = !returnToTop && (key !== 'ready' || this.busy);
          if (returnToTop)
            button.setAttribute('aria-label', `${button.textContent}（${this.config.messages.backToTop}）`);
          else button.removeAttribute('aria-label');
        });
        this.canSubmit = key === 'ready' && !this.busy;
      }
      async submit(event) {
        event.preventDefault();
        if (this.busy) return;
        this.update();
        if (!this.canSubmit || !this.form.reportValidity()) return;
        const selection = this.selection();
        const data = new FormData();
        const items = [this.variant('base')];
        if (selection.lens === 'prescription') items.push(this.variant('lens'));
        if (selection.sun) items.push(this.variant('sun'));
        items.forEach((item, index) => {
          data.append(`items[${index}][id]`, item.id);
          data.append(`items[${index}][quantity]`, '1');
        });
        const notes = this.querySelector('[data-notes]').value.trim();
        if (notes) data.append('items[0][properties][備考]', notes);
        data.append('items[0][properties][視力補正]', selection.lens === 'prescription' ? '補正あり' : '補正なし');
        data.append('items[0][properties][ご注文区分]', '先行予約');
        if (selection.lens === 'prescription') data.append('items[1][properties][処方箋]', this.file.files[0]);
        const status = this.querySelector('[data-status]');
        status.hidden = true;
        this.busy = true;
        this.update();
        try {
          const response = await fetch(this.config.addUrl, {
            method: 'POST',
            body: data,
            headers: { Accept: 'application/json' },
          });
          const result = await response.json();
          if (!response.ok)
            throw new Error(typeof result.description === 'string' ? result.description : this.config.messages.error);
          window.location.assign(this.config.cartUrl);
        } catch (error) {
          status.textContent = error.message || this.config.messages.error;
          status.hidden = false;
          status.scrollIntoView({ block: 'center' });
          this.busy = false;
          this.update();
        }
      }
    },
  );
}

if (!customElements.get('store-lp-media-gallery')) {
  customElements.define(
    'store-lp-media-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.index = 1;
        this.playing = false;
        this.paused = false;
        this.visible = false;
        this.hovered = false;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.paused = this.motion.matches;
        this.motion.addEventListener(
          'change',
          () => {
            if (this.motion.matches) this.paused = true;
            this.schedule();
          },
          options,
        );
        this.addEventListener(
          'scroll',
          () => {
            this.index = this.nearest();
            this.schedule();
          },
          { ...options, passive: true },
        );
        this.addEventListener(
          'keydown',
          (event) => {
            if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
            event.preventDefault();
            this.move(event.key === 'ArrowLeft' ? -1 : 1);
          },
          options,
        );
        this.addEventListener(
          'pointerdown',
          (event) => {
            this.suppressClick = false;
            this.interacting = true;
            this.schedule();
            if (event.pointerType === 'mouse' && event.button === 0) {
              this.drag = { x: event.clientX, scroll: this.scrollLeft, id: event.pointerId };
            }
          },
          options,
        );
        this.addEventListener(
          'pointermove',
          (event) => {
            if (!this.drag) return;
            const delta = event.clientX - this.drag.x;
            if (Math.abs(delta) > 6) {
              this.suppressClick = true;
              this.classList.add('is-dragging');
              this.setPointerCapture(event.pointerId);
            }
            if (this.suppressClick) this.scrollLeft = this.drag.scroll - delta;
          },
          options,
        );
        const endDrag = () => {
          if (this.drag && this.hasPointerCapture(this.drag.id)) this.releasePointerCapture(this.drag.id);
          if (this.drag && this.suppressClick) {
            this.index = this.nearest();
            this.classList.remove('is-dragging');
            this.centerSlide();
          }
          this.drag = null;
          this.interacting = false;
          this.schedule();
        };
        window.addEventListener('pointerup', endDrag, options);
        window.addEventListener('pointercancel', endDrag, options);
        this.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType === 'mouse') this.hovered = true;
            this.schedule();
          },
          options,
        );
        this.addEventListener(
          'pointerleave',
          () => {
            this.hovered = false;
            this.schedule();
          },
          options,
        );
        this.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('[data-video-play]');
            if (!button) return;
            if (this.suppressClick) {
              event.preventDefault();
              return;
            }
            this.stopVideos();
            this.index = [...this.children].indexOf(button.parentElement);
            this.centerSlide();
            this.playing = true;
            this.schedule();
            const iframe = document.createElement('iframe');
            iframe.src = button.dataset.videoSrc;
            iframe.title = button.getAttribute('aria-label');
            iframe.allow =
              'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            iframe.allowFullscreen = true;
            button.hidden = true;
            button.after(iframe);
            iframe.focus();
          },
          options,
        );
        for (const node of [this]) {
          node.addEventListener('focusin', () => this.schedule(), options);
          node.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), options);
        }
        document.addEventListener('visibilitychange', () => this.schedule(), options);
        this.resizeObserver = new ResizeObserver(() => this.centerSlide());
        this.resizeObserver.observe(this);
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.visible = entry.isIntersecting;
            this.schedule();
          },
          { threshold: 0.5 },
        );
        this.intersectionObserver.observe(this);
      }
      nearest() {
        const center = this.getBoundingClientRect().left + this.clientWidth / 2;
        return [...this.children].reduce((best, slide, index, slides) => {
          const distance = (el) => {
            const r = el.getBoundingClientRect();
            return Math.abs(r.left + r.width / 2 - center);
          };
          return distance(slide) < distance(slides[best]) ? index : best;
        }, 0);
      }
      schedule() {
        clearTimeout(this.timer);
        if (
          this.paused ||
          this.motion.matches ||
          this.playing ||
          !this.visible ||
          this.hovered ||
          this.interacting ||
          document.hidden ||
          this.matches(':focus-within')
        )
          return;
        this.timer = setTimeout(() => this.move(1), 5000);
      }
      stopVideos() {
        this.querySelectorAll('iframe').forEach((frame) => frame.remove());
        this.querySelectorAll('[data-video-play]').forEach((button) => {
          button.hidden = false;
        });
        this.playing = false;
      }
      move(delta) {
        this.stopVideos();
        this.index = (this.index + delta + this.children.length) % this.children.length;
        this.centerSlide(this.motion.matches ? 'instant' : 'smooth');
        this.schedule();
      }
      centerSlide(behavior = 'instant') {
        const slide = this.children[this.index];
        if (!slide) return;
        const frame = this.getBoundingClientRect();
        const rect = slide.getBoundingClientRect();
        this.scrollTo({
          left: this.scrollLeft + rect.left - frame.left - (this.clientWidth - rect.width) / 2,
          behavior,
        });
      }
      disconnectedCallback() {
        clearTimeout(this.timer);
        this.controller?.abort();
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
      }
    },
  );
}
